

































































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

import { curCoreClient, CoreClient, UpdateUserReq } from '@/services/CoreClient';
import { Device, Info, User } from '@/types/core';
import { DateTime } from 'luxon';

import Loading from '@/components/Loading.vue';
import VueError from '@/components/VueError.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import Success from '@/components/Success.vue';
import ConfirmedAction from '@/components/ConfirmedAction.vue';
import GroupAccessEdit from '@/components/access/GroupAccessEdit.vue';
import GroupAccessEditSmall from '@/components/access/GroupAccessEditSmall.vue';

import moment from 'moment';

@Component({
  components: {
    Loading,
    VueError,
    SectionHeader,
    Success,
    ConfirmedAction,
    GroupAccessEdit,
    GroupAccessEditSmall
  },
})
export default class EditUser extends Vue {

  // our model
  info:Info = {} as Info;
  
  @Prop({ default: {},  type: Object}) user!: User;
  @Prop({ default: 'edit',  type: String}) mode!: 'edit'|'change-password'|'delete';

  // our model
  dateTime = DateTime;

  dpValidFromTo:{start?:string, end?:string, shortcut?:string} = {};

  // password
  newPassword: string = '';
  repeatPassword: string = '';
  
  httpBusy:boolean = false;
  success:string = '';
  error:string = '';

  // internal dependencies
  private coreClient:CoreClient = curCoreClient;
  
  async mounted() {

    const reply = await this.coreClient.getInfo();
    
    if(reply.httpCode === 403)
      return this.$router.push({name:'login'});

    if(reply.errorMsg)
      return this.error = `Unable to retrieve user info: ${reply.errorMsg}`;
    
    // we update the state
    this.info = reply.info as Info;

    // we update the validFrom and to if they are both set
    if(this.user.validFrom && this.user.validTo) {
      this.dpValidFromTo.start = moment(this.user.validFrom).format('YYYY-MM-DD hh:mm a');
      this.dpValidFromTo.end = moment(this.user.validTo).format('YYYY-MM-DD hh:mm a');
    }
  }

  groupIdListUpdate(newGroupIdList:string[]) {
    
    this.user.groupIdList = newGroupIdList.concat();
  }

  get canEdit():boolean {
    return !this.httpBusy && this.user.groupIdList.length > 0;
  }

  get canCancel():boolean {
    return !this.httpBusy;
  }

  async edit() {

    this.httpBusy = true;
    this.error = '';
    
    const req:UpdateUserReq = {
      email: this.user.email,
      status: this.user.status,
      level: this.user.level,
      groupIdList: this.user.groupIdList
    }

    if(this.dpValidFromTo && this.dpValidFromTo.start && this.dpValidFromTo.end) {
      req.validFrom = moment(this.dpValidFromTo.start, "YYYY-MM-DD hh:mm a").valueOf(),
      req.validTo = moment(this.dpValidFromTo.end, "YYYY-MM-DD hh:mm a").valueOf()
    } else {
      req.validFrom = null;
      req.validTo = null;
    }

    const reply = await this.coreClient.updateUser(req);

    this.httpBusy = false;
    
    if(reply.httpCode === 403)
      return this.$router.push({name:'login'});

    if(reply.errorMsg)
      return this.error = `Unable to modify user: ${reply.errorMsg}`;
    
    this.setSuccess(`User ${this.user.email} correctly updated`);
  }

  get canChangePassword():boolean {
    return !this.httpBusy;
  }

  async changePassword() {

    this.httpBusy = true;
    this.error = '';
    
    const reply = await this.coreClient.updateUser({
      email: this.user.email,
      password: this.newPassword
    });

    this.httpBusy = false;
    
    if(reply.httpCode === 403)
      return this.$router.push({name:'login'});

    if(reply.errorMsg)
      return this.error = `Unable to modify user: ${reply.errorMsg}`;
    
    this.setSuccess(`User ${this.user.email} correctly updated`);
  }

  async deleteUser() {

    const reply = await this.coreClient.deleteUser({email: this.user.email});
    
    if(reply.httpCode === 403)
      return this.$router.push({name:'login'});

    if(reply.errorMsg)
      return this.error = `Unable to delete user: ${reply.errorMsg}`;
    
    this.setSuccess(`User ${this.user.email} has been deleted`);
  }


  setSuccess(str:string) {
    this.success = str;
    setTimeout(() => this.$emit('cancel'), 1000);
  }

  cancel() {
    this.$emit('cancel');
  }

  get dpMinDate():string {
    return moment().format();
  }

  get dpCustomShortcuts():object[] {
    return  [
      { key: 'next7Days', label: 'Next 7 days', value: () => ({start:moment(), end: moment().add(7, 'days')}) },
      { key: 'next30Days', label: 'Next 30 days', value: () => ({start:moment(), end: moment().add(30, 'days')}) },
      { key: 'next90Days', label: 'Next 3 months', value: () => ({start:moment(), end: moment().add(3, 'months')}) },
      { key: 'next180Days', label: 'Next 6 months', value: () => ({start:moment(), end: moment().add(6, 'months')}) }
    ];
  }
}
